/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LogDialogConfig, logDialogRoute } from '@gh/common-internal-shared';
import {
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { DialogService } from '@gh/core-ui';
import { cursorTracker, GridRowEvent, newGridPaginationCursor } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { ButlListBusinessService } from '../../resources/butl-list-business.service';
import { ButlListItem } from '../../resources/butl.model';
import { ButlGridDefinition } from './butl-grid-definition.service';
import { padStart } from 'lodash';
import { currentDate, toString } from '@gh/core-util';

@Component({
  standalone: false,
  selector: 'gh-butl-list',
  templateUrl: 'butl-list.component.html',
  styleUrls: ['butl-list.component.scss'],
  viewProviders: [ButlGridDefinition],
})
export class ButlListComponent implements OnInit {

  dataSource: SlicedDataSource<ButlListItem>;
  back: boolean = false;
  form: FilterableFormState<any>;
  cursor = newGridPaginationCursor();
  defaultStartDate = currentDate();
  defaultStartDateStr = `${currentDate().getFullYear()}/${currentDate().getMonth() + 1}/${currentDate().getDate()}`;
  initialFilter = {
    searchString: '',
    startDate: new Date(this.defaultStartDate.setDate(this.defaultStartDate.getDate() - 14))
      .toISOString().split('T')[0],
    endDate: new Date(this.defaultStartDateStr),
  };

  constructor(private businessService: ButlListBusinessService,
              private route: ActivatedRoute,
              private dialogs: DialogService,
              private routeData: RouteDataService,
              private formStateBuilder: FormStateBuilder,
              public gridDefinition: ButlGridDefinition) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);
    this.gridDefinition.bind(gridLayout);
    this.dataSource = this.businessService.findAllAsDataSource(this.initialFilter);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: this.initialFilter,
      children: {
        searchString: void 0,
        startDate: void 0,
        endDate: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      const endDate = new Date(filter.endDate);
      const startDate = new Date(filter.startDate);
      const fixedFilter = {
        searchString: filter.searchString || '',
        startDate: filter.startDate ? `${[startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate()].map(this.stringifyDateComponent).join('-')}T00:00:00` : null,
        endDate: filter.endDate ? `${[endDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate()].map(this.stringifyDateComponent).join('-')}T23:59:59` : null,
      };
      this.dataSource = this.businessService.findAllAsDataSource(fixedFilter);
    });
  }

  onRowClicked(event: GridRowEvent<ButlListItem>): void {
    this.dialogs.openWithInput(logDialogRoute, <LogDialogConfig>{
      content: this.businessService.get(event.data.id).pipe(map$(({ resultMessage }) => {
        if (!resultMessage || !resultMessage.length) {
          return '';
        }
        const position = resultMessage.toLowerCase().indexOf('processing time:');
        if (position > 0) {
         return resultMessage.substr(0, position) + '\n ' + resultMessage.substr(position);
        }
        return resultMessage;
      })),
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onReset(): void {
    this.form.setValue({
      searchString: this.form.value.searchString,
      startDate: null,
      endDate: null,
    });
    this.form.emitFilter();
  }

  stringifyDateComponent(n: number): string {
    return padStart(toString(n), 2, '0');
  }
}
